import React, { useEffect, useState, useCallback } from "react";
import { Modal, Tabs, Tab, Table, Spinner, Button, Form, Row, Col, Alert as BsAlert } from "react-bootstrap";
import SafetyAppaltiService from "../../../services/safety/SafetyAppaltiService";

const EMPTY_PERS = {
    id_personale: null,
    cognome: "",
    nome: "",
    codice_fiscale: "",
    qualifica: "",
    add_antincendio: 0,
    blsd: 0,
    primo_soccorso: 0,
    fiduciario_ausiliario: 0,
    stuart: 0,
    gpg_armata: 0,
    gpg_non_armata: 0,
    note: "",
};

const CARATTERISTICHE = [
    { key: "add_antincendio",       label: "Addetto antincendio" },
    { key: "blsd",                  label: "BLSD" },
    { key: "primo_soccorso",        label: "Primo soccorso" },
    { key: "fiduciario_ausiliario", label: "Fiduciario / ausiliario sicurezza" },
    { key: "stuart",                label: "Stuart" },
    { key: "gpg_armata",            label: "GPG armata" },
    { key: "gpg_non_armata",        label: "GPG non armata" },
];

const TIPI_DOTAZIONE = [
    { value: "ESTINTORE",         label: "Estintore" },
    { value: "SPRAY_PEPERONCINO", label: "Spray al peperoncino" },
    { value: "RADIO_PORTATILE",   label: "Radio portatile" },
    { value: "TORCIA",            label: "Torcia" },
    { value: "GILET",             label: "Gilet" },
    { value: "GAP",               label: "GAP" },
    { value: "ALTRO",             label: "Altro" },
];

const EMPTY_DOT = {
    id_dotazione: null,
    tipo_dotazione: "ESTINTORE",
    descrizione: "",
    quantita: 1,
    note: "",
};

function dotLabel(v) {
    return TIPI_DOTAZIONE.find(t => t.value === v)?.label || v || "—";
}

function caratteristicheBadges(p) {
    const attive = CARATTERISTICHE.filter(c => Number(p[c.key]) === 1).map(c => c.label);
    if (attive.length === 0) return <span className="text-muted">—</span>;
    return <span className="small">{attive.join(", ")}</span>;
}

export default function AppaltoDettaglioModal({ show, onHide, appalto }) {
    const idAppalto = appalto?.id_appalto;
    const [tab, setTab] = useState("personale");

    const [personale, setPersonale] = useState([]);
    const [dotazioni, setDotazioni] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [persForm, setPersForm] = useState(EMPTY_PERS);
    const [showPers, setShowPers] = useState(false);
    const [dotForm, setDotForm] = useState(EMPTY_DOT);
    const [showDot, setShowDot] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadAll = useCallback(async () => {
        if (!idAppalto) return;
        setLoading(true);
        setError(null);
        try {
            const [rp, rd] = await Promise.all([
                SafetyAppaltiService.personaleByAppalto(idAppalto),
                SafetyAppaltiService.dotazioniByAppalto(idAppalto),
            ]);
            const dp = rp && typeof rp.json === "function" ? await rp.json() : rp;
            const dd = rd && typeof rd.json === "function" ? await rd.json() : rd;
            setPersonale(Array.isArray(dp) ? dp : []);
            setDotazioni(Array.isArray(dd) ? dd : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento dei dettagli appalto.");
        } finally {
            setLoading(false);
        }
    }, [idAppalto]);

    useEffect(() => {
        if (show && idAppalto) {
            setTab("personale");
            setShowPers(false);
            setShowDot(false);
            loadAll();
        }
    }, [show, idAppalto, loadAll]);

    const onPersField = (e) => {
        const { name, value, type, checked } = e.target;
        setPersForm(f => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
    };
    const openNewPers = () => { setPersForm(EMPTY_PERS); setShowPers(true); };
    const openEditPers = (p) => { setPersForm({ ...EMPTY_PERS, ...p }); setShowPers(true); };

    const savePers = async () => {
        if (!persForm.cognome?.trim() || !persForm.nome?.trim()) {
            setError("Cognome e nome del personale sono obbligatori.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await SafetyAppaltiService.personaleSave({ ...persForm, id_appalto: idAppalto });
            setShowPers(false);
            setPersForm(EMPTY_PERS);
            await loadAll();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio del personale.");
        } finally {
            setSaving(false);
        }
    };

    const deletePers = async (p) => {
        if (!window.confirm(`Eliminare ${p.cognome} ${p.nome} dall'elenco?`)) return;
        try {
            await SafetyAppaltiService.personaleDelete(p.id_personale);
            await loadAll();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    const onDotField = (e) => {
        const { name, value } = e.target;
        setDotForm(f => ({ ...f, [name]: value }));
    };
    const openNewDot = () => { setDotForm(EMPTY_DOT); setShowDot(true); };
    const openEditDot = (d) => { setDotForm({ ...EMPTY_DOT, ...d }); setShowDot(true); };

    const saveDot = async () => {
        setSaving(true);
        setError(null);
        try {
            await SafetyAppaltiService.dotazioniSave({ ...dotForm, id_appalto: idAppalto });
            setShowDot(false);
            setDotForm(EMPTY_DOT);
            await loadAll();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio della dotazione.");
        } finally {
            setSaving(false);
        }
    };

    const deleteDot = async (d) => {
        if (!window.confirm(`Eliminare la dotazione "${dotLabel(d.tipo_dotazione)}"?`)) return;
        try {
            await SafetyAppaltiService.dotazioniDelete(d.id_dotazione);
            await loadAll();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title className="h6 mb-0">
                    <i className="bi bi-people me-2" />
                    Personale e dotazione appalto
                    {appalto?.ragione_sociale_impresa ? ` — ${appalto.ragione_sociale_impresa}` : ""}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <BsAlert variant="danger" className="py-2 small" onClose={() => setError(null)} dismissible>{error}</BsAlert>}
                {loading ? (
                    <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
                ) : (
                    <Tabs activeKey={tab} onSelect={(k) => setTab(k || "personale")} className="mb-3">
                        <Tab eventKey="personale" title={<span><i className="bi bi-person-vcard me-1" />Personale ({personale.length})</span>}>
                            <div className="d-flex justify-content-end mb-2">
                                <Button size="sm" variant="primary" onClick={openNewPers} disabled={showPers}>
                                    <i className="bi bi-plus-lg me-1" />Aggiungi addetto
                                </Button>
                            </div>

                            {showPers && (
                                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <Row className="g-2">
                                        <Col xs={12} md={4}>
                                            <Form.Label className="small mb-0">Cognome *</Form.Label>
                                            <Form.Control size="sm" name="cognome" value={persForm.cognome} onChange={onPersField} />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Label className="small mb-0">Nome *</Form.Label>
                                            <Form.Control size="sm" name="nome" value={persForm.nome} onChange={onPersField} />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Label className="small mb-0">Codice fiscale</Form.Label>
                                            <Form.Control size="sm" name="codice_fiscale" value={persForm.codice_fiscale} onChange={onPersField} />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Label className="small mb-0">Qualifica</Form.Label>
                                            <Form.Control size="sm" name="qualifica" value={persForm.qualifica} onChange={onPersField} />
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Label className="small mb-1 d-block">Caratteristiche dipendente</Form.Label>
                                            <Row className="g-1">
                                                {CARATTERISTICHE.map(c => (
                                                    <Col xs={6} md={4} key={c.key}>
                                                        <Form.Check type="checkbox" label={c.label} name={c.key}
                                                            checked={Number(persForm[c.key]) === 1} onChange={onPersField} />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Label className="small mb-0">Note</Form.Label>
                                            <Form.Control as="textarea" rows={2} size="sm" name="note" value={persForm.note} onChange={onPersField} />
                                        </Col>
                                    </Row>
                                    <div className="mt-2 d-flex gap-2">
                                        <Button size="sm" variant="primary" onClick={savePers} disabled={saving}>
                                            {saving ? <Spinner size="sm" animation="border" /> : "Salva addetto"}
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setShowPers(false)} disabled={saving}>Annulla</Button>
                                    </div>
                                </div>
                            )}

                            {personale.length === 0 ? (
                                <div className="text-muted small">Nessun addetto inserito per questo appalto.</div>
                            ) : (
                                <Table size="sm" bordered hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Nominativo</th>
                                            <th>Qualifica</th>
                                            <th>Caratteristiche</th>
                                            <th className="text-end">Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {personale.map(p => (
                                            <tr key={p.id_personale}>
                                                <td className="fw-semibold">{p.cognome} {p.nome}</td>
                                                <td className="small">{p.qualifica || "—"}</td>
                                                <td>{caratteristicheBadges(p)}</td>
                                                <td className="text-end">
                                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditPers(p)}>
                                                        <i className="bi bi-pencil" />
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => deletePers(p)}>
                                                        <i className="bi bi-trash" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Tab>

                        <Tab eventKey="dotazioni" title={<span><i className="bi bi-box-seam me-1" />Dotazione ({dotazioni.length})</span>}>
                            <div className="d-flex justify-content-end mb-2">
                                <Button size="sm" variant="primary" onClick={openNewDot} disabled={showDot}>
                                    <i className="bi bi-plus-lg me-1" />Aggiungi dotazione
                                </Button>
                            </div>

                            {showDot && (
                                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <Row className="g-2">
                                        <Col xs={12} md={5}>
                                            <Form.Label className="small mb-0">Tipo dotazione</Form.Label>
                                            <Form.Select size="sm" name="tipo_dotazione" value={dotForm.tipo_dotazione} onChange={onDotField}>
                                                {TIPI_DOTAZIONE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col xs={12} md={5}>
                                            <Form.Label className="small mb-0">Descrizione</Form.Label>
                                            <Form.Control size="sm" name="descrizione" value={dotForm.descrizione} onChange={onDotField} />
                                        </Col>
                                        <Col xs={12} md={2}>
                                            <Form.Label className="small mb-0">Quantità</Form.Label>
                                            <Form.Control type="number" min="0" size="sm" name="quantita" value={dotForm.quantita} onChange={onDotField} />
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Label className="small mb-0">Note</Form.Label>
                                            <Form.Control as="textarea" rows={2} size="sm" name="note" value={dotForm.note} onChange={onDotField} />
                                        </Col>
                                    </Row>
                                    <div className="mt-2 d-flex gap-2">
                                        <Button size="sm" variant="primary" onClick={saveDot} disabled={saving}>
                                            {saving ? <Spinner size="sm" animation="border" /> : "Salva dotazione"}
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setShowDot(false)} disabled={saving}>Annulla</Button>
                                    </div>
                                </div>
                            )}

                            {dotazioni.length === 0 ? (
                                <div className="text-muted small">Nessuna dotazione assegnata a questo appalto.</div>
                            ) : (
                                <Table size="sm" bordered hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Descrizione</th>
                                            <th className="text-end">Quantità</th>
                                            <th className="text-end">Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dotazioni.map(d => (
                                            <tr key={d.id_dotazione}>
                                                <td className="fw-semibold">{dotLabel(d.tipo_dotazione)}</td>
                                                <td className="small">{d.descrizione || "—"}</td>
                                                <td className="text-end">{d.quantita}</td>
                                                <td className="text-end">
                                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditDot(d)}>
                                                        <i className="bi bi-pencil" />
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => deleteDot(d)}>
                                                        <i className="bi bi-trash" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Tab>
                    </Tabs>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Chiudi</Button>
            </Modal.Footer>
        </Modal>
    );
}
